import React, { useEffect, useState } from "react";
import { Row, Col, Form } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import SearchDrawer from "./SearchDrawer";

const OrderDetails = ({
  details,
  setDetails,
  type,
  lineItems,
  mode,
  ledgersList,
  showErrors = {
    partyLedger: false,
    salesLedger: false,
  },
}) => {
  const [drawer, setDrawer] = useState({ show: false, field: "" });
  const isEdit = (mode || "").toLowerCase() === "edit";

  const [partyLedgers, setPartyLedgers] = useState([]);
  const [salesLedgers, setSalesLedgers] = useState([]);

  // Split incoming ledgersList into party vs sales
  useEffect(() => {
    if (Array.isArray(ledgersList)) {
      setSalesLedgers(
        ledgersList.filter((r) => r.groupLabel === "Sales Accounts")
      );
      setPartyLedgers(
        ledgersList.filter(
          (r) =>
            r.groupLabel === "Sundry Debtors" ||
            r.groupLabel === "Sundry Creditors"
        )
      );
    }
  }, [ledgersList]);

  const openDrawer = (field) => setDrawer({ show: true, field });
  const closeDrawer = () => setDrawer({ show: false, field: "" });

  const totalAmount = (lineItems || [])
    .reduce((sum, i) => sum + (i.amount || 0), 0)
    .toFixed(2);

  const displayLedger = (ledger, placeholder) => ledger?.label || placeholder;

  return (
    <>
      <h6 className="fw-bold border-bottom pb-2 mb-3">Order Details</h6>
      <Row className="g-3">
        {/* Order Date */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Order Date</Form.Label>
            <Form.Control
              type="date"
              value={details.date || ""}
              onChange={(e) => setDetails({ ...details, date: e.target.value })}
            />
          </Form.Group>
        </Col>

        {/* Order Number */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Order Number</Form.Label>
            <Form.Control value={details.number || ""} disabled readOnly />
            <Form.Text muted>Order number is auto-generated</Form.Text>
          </Form.Group>
        </Col>

        {/* Type */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Type</Form.Label>
            <Form.Control
              value={
                type
                  ? type.charAt(0).toUpperCase() + type.slice(1).toLowerCase()
                  : ""
              }
              disabled
              readOnly
            />
            <Form.Text muted>Type cannot be changed after selection</Form.Text>
          </Form.Group>
        </Col>

        {/* Status */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={details.status || "Pending"}
              onChange={(e) =>
                setDetails({ ...details, status: e.target.value })
              }
            >
              {details.status === "Completed" && mode === "edit" ? (
            <>
              <option value="Completed">Completed</option>
              <option value="Canceled">Canceled</option>
            </>
                ) : (
            <>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Draft">Draft</option>
              <option value="Canceled">Canceled</option>
            </>
            )}
            </Form.Select>
          </Form.Group>
        </Col>

        {/* Party Ledger */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              Party Ledger <span className="text-danger">*</span>
            </Form.Label>
            <div
              className={`form-control d-flex justify-content-between align-items-center ${
                isEdit ? "bg-light text-muted" : "bg-white"
              } ${
                showErrors.partyLedger && !details.partyLedger?.value
                  ? "border-danger"
                  : ""
              }`} // ✅ add red border
              style={{ cursor: isEdit ? "not-allowed" : "pointer" }}
              onClick={() => !isEdit && openDrawer("partyLedger")}
            >
              <span
                className={`text-truncate ${
                  details.partyLedger ? "text-dark" : "text-muted"
                }`}
              >
                {displayLedger(details.partyLedger, "Search Party Ledger...")}
              </span>
              <FaSearch className="text-muted ms-2" />
            </div>
            {/* ✅ show error text only when needed */}
            {showErrors.partyLedger && !details.partyLedger?.value && (
              <small className="text-danger">Party Ledger is required</small>
            )}
          </Form.Group>
        </Col>

        {/* Sales Ledger */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              Sales Ledger <span className="text-danger">*</span>
            </Form.Label>
            <div
              className={`form-control d-flex justify-content-between align-items-center ${
                showErrors.salesLedger && !details.salesLedger?.value
                  ? "border-danger"
                  : ""
              }`} // ✅ red border
              style={{ cursor: "pointer" }}
              onClick={() => openDrawer("salesLedger")}
            >
              <span
                className={`text-truncate ${
                  details.salesLedger ? "text-dark" : "text-muted"
                }`}
              >
                {displayLedger(details.salesLedger, "Search Sales Ledger...")}
              </span>
              <FaSearch className="text-muted ms-2" />
            </div>
            {/* ✅ error text */}
            {showErrors.salesLedger && !details.salesLedger?.value && (
              <small className="text-danger">Sales Ledger is required</small>
            )}
          </Form.Group>
        </Col>

        {/* Total Amount */}
        <Col md={3}>
          <Form.Group>
            <Form.Label>Total Amount</Form.Label>
            <Form.Control value={totalAmount} disabled readOnly />
          </Form.Group>
        </Col>
      </Row>

      {/* Drawer */}
      <SearchDrawer
        show={drawer.show}
        onHide={closeDrawer}
        title={
          drawer.field === "partyLedger"
            ? "Select Party Ledger"
            : "Select Sales Ledger"
        }
        options={drawer.field === "partyLedger" ? partyLedgers : salesLedgers}
        onSelect={(val) => setDetails({ ...details, [drawer.field]: val })}
      />
    </>
  );
};

export default OrderDetails;
